import { useEffect, useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { EmptyState } from '../../../../components/EmptyState';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { surveyQuestionnairesApi } from '../surveyApi';
import { SURVEY_STATUS, type Survey, type SurveyQuestionnaire, type SurveyStatusValue } from '../surveyTypes';
import { SURVEY_ACTS, type SurveyAct } from '../utils/surveyActs';
import { SurveyAnswerModal } from './SurveyAnswerModal';
import { SurveyQuestionnaireModal } from './SurveyQuestionnaireModal';
import { SurveyQuestionnaireView } from './SurveyQuestionnaireView';

/**
 * Survey → Configuration › Questionnaire. A survey holds ONE live questionnaire (product call,
 * 09 Oct 2026; the server refuses a second on create and on restore), so the panel is never a
 * list:
 *
 * - WITH it, the panel IS that questionnaire — its questions on arrival
 *   (`<SurveyQuestionnaireView>`), with Edit, Answer and Delete behind its cog.
 * - WITHOUT it, one centred Create and nothing else — no search bar and no table (product call,
 *   the same day). The cost, accepted: a deleted questionnaire has no Restore on screen; the
 *   endpoint stays, and a new one is made with Create.
 *
 * Reading takes the page's `surveys.read`; creating, editing and deleting take `surveys.update`,
 * and a deleted survey refuses all three, so the controls say why rather than failing on Submit.
 */
export function SurveyQuestionnairesPanel({
  surveyId,
  surveyActive,
  surveyStatus,
  onChanged,
  onSurveyChanged,
}: {
  surveyId: string;
  surveyActive: boolean;
  /** Where the survey is in its run — the answer form says why it is closed outside Started. */
  surveyStatus: SurveyStatusValue;
  /** After any write here — the page re-reads the survey's counts, which gate its tabs. */
  onChanged?: () => void;
  /** After the survey is started, ended or re-opened from this card's cog. */
  onSurveyChanged?: (next: Survey) => void;
}) {
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SurveyQuestionnaire | null>(null);
  const [answering, setAnswering] = useState<SurveyQuestionnaire | null>(null);
  const [deleting, setDeleting] = useState<SurveyQuestionnaire | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // The survey's live questionnaire: undefined while asking, null when it has none. Re-asked
  // whenever a write bumps `version`.
  const [live, setLive] = useState<SurveyQuestionnaire | null | undefined>(undefined);
  const [version, setVersion] = useState(0);

  const deletedSurvey = surveyActive ? null : 'This survey is deleted. Restore it before changing its questionnaires.';

  useEffect(() => {
    const controller = new AbortController();

    surveyQuestionnairesApi
      .list(surveyId, { pageSize: 1, status: 'active' }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setLive(page.items[0] ?? null);
      })
      .catch(() => {
        // The server is the authority on the one-per-survey rule; a failed read offers Create,
        // and a second questionnaire is refused there.
        if (!controller.signal.aborted) setLive((current) => (current === undefined ? null : current));
      });

    return () => controller.abort();
  }, [surveyId, version]);

  const changed = () => {
    setVersion((v) => v + 1);
    onChanged?.();
  };

  /*
    START / END THE SURVEY from the questionnaire's own cog (product call, 13 Oct 2026) — the ONE
    act its status allows: Start before it has started, End while it runs, Re-open once ended.
    The same three acts, wording and confirmation as Info › Survey status; on `surveys.update`.
  */
  const [act, setAct] = useState<SurveyAct | null>(null);

  const nextAct: SurveyAct =
    surveyStatus === SURVEY_STATUS.Started ? 'end' : surveyStatus === SURVEY_STATUS.Done ? 'reopen' : 'start';

  async function runAct() {
    if (!act) return;

    setRunning(true);
    setError(null);

    try {
      const next = await SURVEY_ACTS[act].run(surveyId);

      toast.success(SURVEY_ACTS[act].done);
      setAct(null);
      onSurveyChanged?.(next);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  async function runDelete() {
    if (!deleting) return;

    setRunning(true);
    setError(null);

    try {
      await surveyQuestionnairesApi.deactivate(surveyId, deleting.id);

      toast.success(`Deleted "${deleting.title}".`);
      setDeleting(null);
      changed();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-w-0 flex-col">
      {live === undefined ? (
        <Skeleton className="m-4 h-40" />
      ) : live ? (
        <SurveyQuestionnaireView
          surveyId={surveyId}
          questionnaire={live}
          version={version}
          deletedSurvey={deletedSurvey}
          onEdit={() => setEditing(live)}
          onAnswer={() => setAnswering(live)}
          onDelete={() => setDeleting(live)}
          statusItems={[
            {
              key: nextAct,
              label: SURVEY_ACTS[nextAct].title,
              permission: 'surveys.update',
              disabledReason: surveyActive ? null : 'This survey is deleted. Restore it before changing its status.',
              onClick: () => setAct(nextAct),
            },
          ]}
        />
      ) : (
        <div className="flex h-full items-center justify-center">
          <EmptyState
            message={deletedSurvey ?? 'This survey has no questionnaire yet.'}
            action={{
              label: 'Create',
              onClick: () => setCreating(true),
              permission: 'surveys.update',
              disabled: deletedSurvey != null,
            }}
          />
        </div>
      )}

      {creating || editing !== null ? (
        <SurveyQuestionnaireModal
          open
          surveyId={surveyId}
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            toast.success(editing ? 'Saved the questionnaire.' : 'Added the questionnaire.');
            setCreating(false);
            setEditing(null);
            changed();
          }}
        />
      ) : null}

      {answering ? (
        <SurveyAnswerModal
          open
          surveyId={surveyId}
          surveyActive={surveyActive}
          surveyStatus={surveyStatus}
          questionnaire={answering}
          onClose={() => setAnswering(null)}
          onAnswered={() => {
            setAnswering(null);
            changed();
          }}
        />
      ) : null}

      {act ? (
        <ConfirmDialog
          open
          onClose={() => {
            setAct(null);
            setError(null);
          }}
          onConfirm={() => void runAct()}
          title={SURVEY_ACTS[act].title}
          requireConfirm
          loading={running}
          id={`survey-questionnaire-${act}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>{SURVEY_ACTS[act].consequence}</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(null);
            setError(null);
          }}
          onConfirm={() => void runDelete()}
          title="Delete questionnaire"
          danger
          requireConfirm
          requireCredential
          loading={running}
          id="survey-questionnaire-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {`"${deleting.title}" is deleted, and Create is offered in its place. Nothing is erased — its responses are kept.`}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
